import dayjs from 'dayjs';
import type { Debtor } from 'types/store/client-state';
import { z } from 'zod/v4';
import type { CustomProduct } from './InvoiceForm.types';

const StringOrNumber = z.union([z.string(), z.number()]);

const FormDateSchema = z
  .string()
  .nullable()
  .refine(value => !!value && dayjs(value).isValid(), { message: 'required' });

export const CustomProductLineSchema = z.object({
  id: StringOrNumber.optional(),
  product_id: StringOrNumber.optional(),
  // Backend returns null on lines that aren't credit-note-linked invoices.
  linked_invoice_id: z.number().nullable().optional(),
  quantity: StringOrNumber,
  discount_rate: StringOrNumber.optional(),
  // Backend returns these as BigDecimal-encoded strings on load.
  total_htva: StringOrNumber,
  total_tvac: StringOrNumber,
  isInvoice: z.boolean(),
  product_attributes: z.custom<Partial<CustomProduct>>(value => {
    return typeof value === 'object' && value !== null;
  }),
});

const baseInvoiceFormSchema = z.object({
  debtor_attributes: z
    .custom<Debtor>(value => typeof value === 'object' && value !== null)
    .optional(),
  billing_log_id: StringOrNumber.nullable().optional(),
  issue_date: FormDateSchema,
  due_date: FormDateSchema,
  // Backend returns `comment: null` for credit notes without a description.
  comment: z.string().nullable().optional(),
  // Backend returns `order_reference: null` for invoices/credit notes without one.
  order_reference: z.string().nullable().optional(),
  invoices_to_link: z.array(StringOrNumber).optional(),
  product_lines_attributes: z.array(CustomProductLineSchema).default([]),
});

export const InvoiceFormSchema = baseInvoiceFormSchema;

export const InvoiceFormSubmitSchema = baseInvoiceFormSchema.extend({
  debtor_attributes: z.custom<Debtor>(value => typeof value === 'object' && value !== null, {
    message: 'required',
  }),
  billing_log_id: StringOrNumber.nullable().refine(value => value != null && value !== '', {
    message: 'required',
  }),
});
